/**
 * "Change format…" (ADR 0036): adapt a graphic to another canvas size, e.g. a 16:9 graphic duplicated in PRISM to
 * vertical 9:16, so it needs touch-ups instead of a redesign. Pure; the editor applies it as one undoable command.
 *
 * Times are milliseconds, so a new frame rate needs no timeline change. Lengths are canvas pixels: every length the
 * scene has (the table in scene-schema's lengths.ts, keyframes included) is multiplied by one uniform factor `s`,
 * which keeps the content's proportions:
 * - 'scale' (the default when the shape stays, HD → UHD): s = newWidth / oldWidth, so the graphic is the same at
 *   another size;
 * - 'reposition' (the default when the shape changes, 16:9 → 9:16): s is the "Content size" (by default the
 *   largest that fits both ways, min(newW/oldW, newH/oldH) rounded down to a sixteenth; 1 keeps the size).
 * Then each element outside a stack is placed in its new parent box by its anchor: left/top, right/bottom and
 * center/middle keep their (scaled) offset from that edge or the centre, which their x/y already are; stretch
 * fills the new size with its scaled insets; an element without an anchor keeps its centre at the same fraction
 * of the box. What ends up outside graphics-safe shows in the Problems panel.
 *
 * Shared by the editor ("Change format…", adding a layout) and the API (a template instantiated in a shape it has no
 * layout for, ADR 0037). Pure.
 */
import { aspectOf, rateFor } from './formats.ts';
import { timelinePhases } from './layouts.ts';
import { ELEMENT_NUMBERS, LENGTH_TRACK_PROPERTIES } from './lengths.ts';
import type { Element, Scene } from './scene.ts';
import type { FrameRate } from './time.ts';

export type ContentMode = 'scale' | 'reposition';

export interface AdaptTarget {
  width: number;
  height: number;
  /** Default: the current rate when the new format allows it, otherwise the format's first. */
  frameRate?: FrameRate;
}

export interface AdaptOptions {
  content: ContentMode;
  /** 'reposition': the uniform content scale (default `defaultContentScale`). Ignored by 'scale'. */
  contentScale?: number;
  /** An element's laid-out size before the change (auto-size boxes and text), for placing unanchored ones. */
  sizeOf?: (id: string) => { width: number; height: number } | undefined;
}

type Size = { width: number; height: number };

/** 'scale' when the shape stays (16:9 → 16:9), 'reposition' when it changes. */
export function defaultContent(from: Size, to: Size): ContentMode {
  const a = aspectOf(from);
  if (a !== 'other') return a === aspectOf(to) ? 'scale' : 'reposition';
  return Math.abs(from.width / from.height / (to.width / to.height) - 1) <= 0.01 ? 'scale' : 'reposition';
}

/** The largest content scale that fits both ways, rounded down to a sixteenth (16:9 → 9:16: 9/16). */
export function defaultContentScale(from: Size, to: Size): number {
  const fit = Math.min(to.width / from.width, to.height / from.height);
  return Math.max(1 / 16, Math.floor(fit * 16 + 1e-9) / 16);
}

/** The factor lengths are multiplied by. */
export function contentScaleOf(from: Size, to: Size, opts: Pick<AdaptOptions, 'content' | 'contentScale'>): number {
  return opts.content === 'scale' ? to.width / from.width : opts.contentScale ?? defaultContentScale(from, to);
}

const round = (n: number) => Math.round(n * 1e4) / 1e4;

/** Multiply the number(s) at a dotted path; arrays on the way (and at the end) are walked through. */
function scalePath(obj: unknown, path: readonly string[], s: number): void {
  if (Array.isArray(obj)) {
    for (const item of obj) scalePath(item, path, s);
    return;
  }
  if (typeof obj !== 'object' || obj === null) return;
  const rec = obj as Record<string, unknown>;
  const [head, ...rest] = path;
  const v = rec[head!];
  if (rest.length) return scalePath(v, rest, s);
  if (typeof v === 'number') rec[head!] = round(v * s);
  else if (Array.isArray(v)) rec[head!] = v.map((x) => (typeof x === 'number' ? round(x * s) : x));
}

/** Scale an element's own lengths (not its children's) in place. */
function scaleLengths(el: Element, s: number): void {
  for (const p of ELEMENT_NUMBERS[el.type].lengths) scalePath(el, p.split('.'), s);
}

/** Moves of unanchored elements after scaling (canvas px), applied to their absolute x/y keyframes too. */
type Shift = Map<string, { dx: number; dy: number }>;

/**
 * Place `children` (already scaled) in their parent's new box. Boxes are undefined where the parent's size is not a
 * number (auto-size boxes hug their content: scaling already placed the children); `canvas` is for anchor.to canvas.
 */
function place(children: Element[], box: { from?: Size; to?: Size }, canvas: { from: Size; to: Size }, s: number, original: Map<string, Element>, opts: AdaptOptions, shift: Shift): void {
  for (const el of children) {
    const a = el.anchor;
    const b = a?.to === 'canvas' ? canvas : box;
    const before = original.get(el.id)!;
    if (b.from && b.to) {
      const measured = opts.sizeOf?.(el.id);
      const w0 = typeof before.size.width === 'number' ? before.size.width : measured?.width ?? 0;
      const h0 = typeof before.size.height === 'number' ? before.size.height : measured?.height ?? 0;
      let dx = 0;
      let dy = 0;
      if (!a) {
        const x = round(((before.transform.x + w0 / 2) / b.from.width) * b.to.width - (w0 * s) / 2);
        const y = round(((before.transform.y + h0 / 2) / b.from.height) * b.to.height - (h0 * s) / 2);
        dx = x - el.transform.x;
        dy = y - el.transform.y;
        el.transform = { ...el.transform, x, y };
      } else {
        // Stretch: the scaled insets stay, the size follows the box (auto sizes stretch by themselves).
        if (a.horizontal === 'stretch' && typeof before.size.width === 'number') el.size = { ...el.size, width: Math.max(0, round(b.to.width - (b.from.width - before.size.width) * s)) };
        if (a.vertical === 'stretch' && typeof before.size.height === 'number') el.size = { ...el.size, height: Math.max(0, round(b.to.height - (b.from.height - before.size.height) * s)) };
      }
      if (dx || dy) shift.set(el.id, { dx, dy });
    }
    if ((el.type === 'group' && el.layout.mode === 'absolute') || el.type === 'clipMask') {
      const numeric = (z: Element['size']) => (typeof z.width === 'number' && typeof z.height === 'number' ? { width: z.width, height: z.height } : undefined);
      const from = numeric(before.size);
      const to = numeric(el.size);
      // Children are anchored in the padding box.
      const pad = (z: Size | undefined, p: { top: number; right: number; bottom: number; left: number } | undefined) => (z && p ? { width: z.width - p.left - p.right, height: z.height - p.top - p.bottom } : z);
      const inner = (e: Element, z: Size | undefined) => (e.type === 'group' ? pad(z, e.layout.padding) : z);
      place(el.children, { ...(from ? { from: inner(before, from)! } : {}), ...(to ? { to: inner(el, to)! } : {}) }, canvas, s, original, opts, shift);
    }
  }
}

function scaleTree(elements: Element[], s: number, original: Map<string, Element>): Element[] {
  return elements.map((el) => {
    original.set(el.id, el);
    const copy = structuredClone(el) as Element;
    if (copy.type === 'group' || copy.type === 'clipMask') copy.children = scaleTree(el.type === 'group' || el.type === 'clipMask' ? el.children : [], s, original);
    scaleLengths(copy, s);
    return copy;
  });
}

/** The scene adapted to `target` (size and frame rate); lengths scaled and elements re-placed as described above. */
export function adaptCanvas(scene: Scene, target: AdaptTarget, opts: AdaptOptions): Scene {
  const from = { width: scene.canvas.width, height: scene.canvas.height };
  const to = { width: target.width, height: target.height };
  const s = contentScaleOf(from, to, opts);
  const original = new Map<string, Element>();
  const elements = scaleTree(scene.elements, s, original);
  const shift: Shift = new Map();
  place(elements, { from, to }, { from, to }, s, original, opts, shift);

  const timeline = structuredClone(scene.timeline);
  const lengths = new Set<string>(LENGTH_TRACK_PROPERTIES);
  for (const [, phase] of timelinePhases(timeline)) {
    for (const tr of phase.tracks) {
      if (!lengths.has(tr.property)) continue;
      const d = !tr.stagger && (tr.property === 'x' || tr.property === 'y') ? shift.get(tr.element)?.[tr.property === 'x' ? 'dx' : 'dy'] ?? 0 : 0;
      tr.keyframes = tr.keyframes.map((k) =>
        typeof k.value === 'number' ? { ...k, value: round(k.value * s + d) } : typeof k.value === 'object' ? { ...k, value: { rel: round(k.value.rel * s) } } : k,
      );
    }
  }
  const frameRate = target.frameRate ?? rateFor(to, scene.canvas.frameRate);
  return { ...scene, elements, timeline, canvas: { ...scene.canvas, ...to, frameRate } };
}
